import type APIResponseData from "~/types/APIResponseData";
import type Schema from "~/types/Schema";
import type APIResponseDataInfo from "~/types/APIResponseDataInfo";
import { debounce } from "perfect-debounce";
import type PaginatedData from "~/types/PaginatedData";
import { DATATYPES } from "~/constants/dataTypes";

export function useSearch() {
  // Data
  const router = useRouter();
  const route = useRoute();

  const dataType = ref("");
  const result = ref([]) as Ref<APIResponseData<Schema>[]>;
  const dataInfo = ref(null) as Ref<APIResponseDataInfo | null>;
  const loading = ref(false);
  const error = ref();

  const search = ref((router.currentRoute.value?.query?.search as string) ?? "");
  const page = ref(1);
  const limit = ref(parseInt(<string>router.currentRoute.value?.query?.limit ?? 10, 10));
  const offset = ref(parseInt(<string>router.currentRoute.value?.query?.offset ?? 1, 10));

  const arySlug = route.params.slug;
  // Set page number from URL
  if (arySlug?.length >= 2 && arySlug[0].toLowerCase() === "page") {
    page.value = parseInt(arySlug[1], 10);
  }

  // Functions
  function isActivePage(pageNumber: number) {
    return pageNumber === page.value;
  }

  function urlWithoutPage() {
    let { path } = router.currentRoute.value;
    path = path.replace(/\/page\/\d+/, ""); // Path without page
    return path;
  }

  function pageUrl(pageNum = 1) {
    const { path, fullPath, query } = router.currentRoute.value;

    const url = urlWithoutPage();
    let queryStr = "";

    if (query.limit) {
      queryStr = `limit=${query.limit}`;
    }
    if (query.search) {
      queryStr = queryStr ? `${queryStr}&search=${query.search}` : `search=${query.search}`;
    }

    // const query = query.substring(query.;
    if (pageNum === 1) {
      return url + (queryStr ? `?${queryStr}` : "");
    }

    return `${url}/page/${pageNum}${queryStr ? `?${queryStr}` : ""}`;
  }

  function updateRouterParams() {
    const query = {} as { search?: string; limit?: number };
    if (search.value) {
      query.search = search.value;
    }
    if (limit.value !== 10) {
      query.limit = limit.value;
    }

    const path = urlWithoutPage() + (page.value > 1 ? `/page/${page.value}` : "");
    router.push({
      path,
      query,
    });
  }

  async function doFetch(updateRouterUrl = false) {
    try {
      if (!dataType.value || DATATYPES[dataType.value] === undefined) {
        throw new Error(`Invalid data type - ${dataType.value}`);
      }

      const params = [];
      params.push(`limit=${limit.value}`);
      if (search.value) {
        params.push(`search=${search.value}`);
      }
      if (page.value > 1) {
        offset.value = (page.value - 1) * limit.value;
        params.push(`offset=${offset.value}`);
      }

      loading.value = true;

      const { data } = await useFetch(`${DATATYPES[dataType.value].apiEndpoint}?${params.join("&")}`);
      result.value = (data.value?.data as APIResponseData<Schema>[]) ?? null;
      dataInfo.value = data.value?.dataInfo ?? null;

      if (updateRouterUrl) {
        updateRouterParams();
      }
    } catch (fetchError) {
      error.value = fetchError;
      console.log("Error:", fetchError);
    } finally {
      loading.value = false;
    }
  }

  function getPaginationObj() {
    if (!dataInfo.value) return null;

    const currentPage = page.value;
    const perPage = limit.value;
    const totalPages = Math.ceil(dataInfo.value.foundCount / perPage) ?? 0;
    const totalItems = dataInfo.value.foundCount ?? 0;

    const pageFirst = 1;
    const pagePrev = currentPage > 1 ? currentPage - 1 : 1;
    const pageNext = currentPage + 1 < totalPages ? currentPage + 1 : totalPages;
    const pageLast = totalPages;

    const links = [] as { label: string; url: string; active: boolean; page: number }[];

    if (totalPages <= 1) {
      links.push({
        label: "Page 1 of 1",
        url: "",
        active: false,
        page: 1,
      });
    } else {
      links.push(
        {
          label: "First",
          url: pageUrl(pageFirst),
          active: isActivePage(pageFirst),
          page: pageFirst,
        },
        {
          label: "Prev",
          url: pageUrl(pagePrev),
          active: isActivePage(pagePrev),
          page: pagePrev,
        },
        {
          label: `Page ${currentPage} of ${totalPages}`,
          url: ``,
          active: false,
          page: currentPage,
        },
        {
          label: "Next",
          url: pageUrl(pageNext),
          active: isActivePage(pageNext),
          page: pageNext,
        },
        {
          label: "Last",
          url: pageUrl(pageLast),
          active: isActivePage(pageLast),
          page: pageLast,
        },
      );
    }
    return {
      pageUrl,
      currentPage,
      perPage,
      totalPages,
      totalItems,
      showItemStart: currentPage === 1 ? 1 : (currentPage - 1) * perPage + 1,
      showItemEnd: currentPage === totalPages ? totalItems : currentPage * perPage,
      links,
    } as PaginatedData;
  }

  const pagination = computed(() => getPaginationObj());
  const debounceFetch = debounce(() => {
    page.value = 1;
    doFetch(true);
  }, 800);

  // Watchers
  watch(search, debounceFetch);
  watch(limit, debounceFetch);

  return {
    dataType,
    search,
    limit,
    result,
    loading,
    error,
    doFetch,
    pagination,
  };
}
